import { useEffect, useState } from "react";
import { FaThumbsUp, FaThumbsDown, FaReply, FaPaperPlane } from "react-icons/fa";
import { Avatar, Button, Message, Spin, TextArea } from "@/frontend/components/common";
import { poolCommentService, type PoolComment } from "@/frontend/services";
import { useAuthStore } from "@/frontend/stores";

interface PoolCommentsTabProps {
  poolId: string;
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "Hace un momento";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Hace ${days} d`;
  return date.toLocaleDateString();
}

export function PoolCommentsTab({ poolId }: PoolCommentsTabProps) {
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState<PoolComment[]>([]);
  const [newText, setNewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await poolCommentService.getByPool(poolId);
      if (response.success && response.data) {
        setComments(response.data);
      } else {
        Message.error({ content: response.error || "Error al cargar los comentarios" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al cargar los comentarios",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [poolId]);

  const handlePost = async (text: string, parentId: string | null) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      setSubmitting(true);
      const response = await poolCommentService.create(poolId, { text: trimmed, parentId });
      if (response.success) {
        if (parentId) {
          setReplyingTo(null);
          setReplyText("");
        } else {
          setNewText("");
        }
        await loadComments();
      } else {
        Message.error({ content: response.error || "Error al enviar el comentario" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al enviar el comentario",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReaction = async (comment: PoolComment, reaction: "like" | "dislike") => {
    const next = comment.userReaction === reaction ? null : reaction;
    try {
      const response = await poolCommentService.setReaction(poolId, comment.id, { reaction: next });
      if (response.success) {
        await loadComments();
      } else {
        Message.error({ content: response.error || "Error al reaccionar al comentario" });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al reaccionar al comentario",
      });
    }
  };

  const renderComment = (comment: PoolComment, depth = 0) => {
    const isOwn = comment.userId === currentUser?.id;
    return (
      <div key={comment.id} className={depth > 0 ? "ml-10 mt-3" : ""}>
        <div className="flex gap-3">
          <Avatar name={comment.author} size={depth > 0 ? 28 : 32} />
          <div className="flex-1">
            <div className="flex items-baseline gap-2">
              <span className="font-medium text-gray-800">
                {comment.author}
                {isOwn && <span className="text-gray-400"> (Tú)</span>}
              </span>
              <span className="text-xs text-gray-400">{timeAgo(comment.createdAt)}</span>
            </div>
            <p className="text-gray-700 mt-1 whitespace-pre-wrap">{comment.text}</p>
            <div className="flex items-center gap-4 mt-1">
              <button
                type="button"
                className={`flex items-center gap-1 text-xs ${
                  comment.userReaction === "like" ? "text-emerald-600" : "text-gray-400"
                } hover:text-emerald-600`}
                onClick={() => handleReaction(comment, "like")}
              >
                <FaThumbsUp /> {comment.likes}
              </button>
              <button
                type="button"
                className={`flex items-center gap-1 text-xs ${
                  comment.userReaction === "dislike" ? "text-red-500" : "text-gray-400"
                } hover:text-red-500`}
                onClick={() => handleReaction(comment, "dislike")}
              >
                <FaThumbsDown /> {comment.dislikes}
              </button>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600"
                onClick={() => {
                  setReplyingTo(replyingTo === comment.id ? null : comment.id);
                  setReplyText("");
                }}
              >
                <FaReply /> Responder
                {comment.replies.length > 0 && (
                  <span className="text-purple-500">({comment.replies.length})</span>
                )}
              </button>
            </div>

            {replyingTo === comment.id && (
              <div className="flex items-end gap-2 mt-2">
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Responder a ${comment.author}...`}
                  className="rounded-full"
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon={<FaPaperPlane />}
                  loading={submitting}
                  onClick={() => handlePost(replyText, comment.id)}
                />
              </div>
            )}

            {comment.replies.map((reply) => renderComment(reply, depth + 1))}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Escribe un comentario..."
          className="rounded-full"
        />
        <Button
          type="primary"
          shape="circle"
          icon={<FaPaperPlane />}
          loading={submitting}
          onClick={() => handlePost(newText, null)}
        />
      </div>

      {comments.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aún no hay comentarios. ¡Sé el primero en escribir!</p>
      ) : (
        <div className="space-y-4 divide-y divide-gray-100">
          {comments.map((comment) => (
            <div key={comment.id} className="pt-4 first:pt-0">
              {renderComment(comment)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
